import { Switch } from '@headlessui/react'
import classNames from 'classnames'
import { useEffect, useState } from 'react'

type Props = {
  label?: string
  defaultValue?: boolean
  handleToggle?: (val: boolean) => Promise<void | boolean> | void | boolean
  showLabelOnLeft?: boolean
  labelClassName?: string
  size?: `sm` | `md` | `lg`
  name?: string
  controlled?: boolean
}

export function Toggle({
  label,
  defaultValue = false,
  handleToggle,
  showLabelOnLeft = false,
  labelClassName,
  size = `md`,
  name,
  controlled,
}: Props) {
  const [enabled, setEnabled] = useState(defaultValue)
  useEffect(() => setEnabled(defaultValue), [defaultValue])
  const onToggle = async (val: boolean) => {
    if (controlled) {
      const result = await handleToggle?.(val)
      if (result === true) {
        setEnabled(val)
      }
    } else {
      setEnabled(val)
      await handleToggle?.(val)
    }
  }

  return (
    <Switch.Group as="div" className="flex items-center">
      <Switch
        name={name}
        checked={enabled}
        disabled={!handleToggle}
        onChange={onToggle}
        className={classNames(
          enabled ? `bg-blue-600` : `bg-surface-strong`,
          size === `sm` && `h-4 w-7`,
          size === `md` && `h-5 w-9`,
          size === `lg` && `h-7 w-12`,
          `relative inline-flex flex-shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:cursor-not-allowed disabled:bg-surface-muted`,
        )}
      >
        <span
          aria-hidden="true"
          className={classNames(
            enabled
              ? {
                  'translate-x-3': size === `sm`,
                  'translate-x-4': size === `md`,
                  'translate-x-[22px]': size === `lg`,
                }
              : `translate-x-0`,
            size === `sm` && `h-3 w-3`,
            size === `md` && `h-4 w-4`,
            size === `lg` && `h-5 w-5`,
            `pointer-events-none inline-block transform rounded-full bg-(--switch-thumb,var(--surface)) shadow ring-0 transition duration-200 ease-in-out`,
          )}
        />
      </Switch>
      {label && (
        <Switch.Label
          as="label"
          className={classNames(`!mb-0 text-ui-xs text-default`, showLabelOnLeft ? `-order-1 mx-2 text-right` : `ml-2`)}
          onClick={() => onToggle(!enabled)}
        >
          <span className={labelClassName}>{label}</span>
        </Switch.Label>
      )}
    </Switch.Group>
  )
}
